(function () {
/*! wordwall — a hidden wall of sayings, revealed by an effect (mouse-light or solo-flash). MIT. Zero dependencies. */

const DEFAULTS = {
  text: 'Say so.',          // string or array of strings
  count: 60,                // phrases on the wall (auto-scaled down for long text)
  layout: 'scatter',        // 'scatter' (jittered grid) | 'rows' (tidy lines)
  gap: 1.5,                 // rows: space between phrases, in em
  rowGap: 2.2,              // rows: line spacing, in em
  angle: 35,                // max rotation in degrees either way; 0 = horizontal only
  sizeMin: 0.75,            // font size range in rem
  sizeMax: 2.2,
  opacityMin: 0.55,
  opacityMax: 1,
  color: '#e8e8e8',
  font: 'inherit',          // CSS font-family; 'inherit' reads the host page's body font
  fontWeight: 400,
  area: 'full',             // 'full' | 'sides' (outside `column`)
  column: null,             // element or selector of the centre column when area = 'sides'
  minSide: 120,             // px; below this side width the wall is off
  columnGap: 0,             // px of clear space kept between the column and the wall
  effect: 'mouse-light',    // 'mouse-light' (spotlight follows the mouse) | 'solo-flash' (one saying lights up at a time)
  every: 3000,              // solo-flash: ms between one saying lighting up and the next
  showMs: 2400,             // solo-flash: how long each saying stays lit, fade in and out included
  radius: 150,              // spotlight radius in px
  intro: false,             // opt-in only: its one-by-one flicker reads as solo-flash, so never on by default
  introCount: 6,
  idleMs: 450,              // mouse-light: ms the spotlight stays after the mouse stops; 'always' = stays while the mouse is over the wall
  zIndex: 0,
  seed: 1,
  className: '',
};

const rand = (seed) => (i, k) => { const x = Math.sin((i + seed * 101) * 12.9898 + k * 78.233) * 43758.5453; return x - Math.floor(x); };
const lerp = (a, b, t) => a + (b - a) * t;

function createWordwall(options = {}) {
  let o = { ...DEFAULTS, ...options };
  const root = document.createElement('div');
  root.className = `wordwall ${o.className}`.trim();
  root.setAttribute('aria-hidden', 'true');
  root.style.cssText = `position:fixed;inset:0;pointer-events:none;overflow:hidden;contain:strict;z-index:${o.zIndex}`;
  // The spotlight is one small canvas: each move copies the wall under the mouse into it and
  // fades the edge. No CSS masks (more reliable in Safari) and no full-screen repaint.
  const lens = document.createElement('canvas');
  lens.style.cssText = 'position:absolute;top:0;left:0;opacity:0;transition:opacity 600ms ease;will-change:transform,opacity';
  root.appendChild(lens);
  const wall = document.createElement('canvas'); // offscreen master, never in the DOM

  let W = 0, H = 0, dpr = 1, phrases = [], clip = null, raf = 0, idle = 0, px = 0, py = 0, alive = true, introAnims = [];
  let tick = 0, lit = null, lastLit = -1;
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const still = matchMedia('(prefers-reduced-motion: reduce)');
  const column = () => (typeof o.column === 'string' ? document.querySelector(o.column) : o.column);
  const fontFamily = () => (o.font === 'inherit' ? getComputedStyle(document.body).fontFamily : o.font);
  const rem = () => parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

  function sidesClip() {
    if (o.area !== 'sides') return { l: [0, W], r: null };
    const c = column();
    if (!c) return null;
    const b = c.getBoundingClientRect();
    if (b.left < o.minSide && W - b.right < o.minSide) return null;
    const g = o.columnGap;
    return { l: [0, Math.max(0, b.left - g)], r: [Math.min(W, b.right + g), W] };
  }

  // Areas the wall covers: the full screen, or the two sides of the centre column.
  const regions = () => (clip.r ? [clip.l, clip.r] : [clip.l]).filter(([a, b]) => b - a > 1);

  function layout(g) {
    const texts = [].concat(o.text).filter(Boolean);
    const r = rand(o.seed), R = rem(), out = [], fam = fontFamily();
    const total = W * H, regs = regions();
    if (o.layout === 'rows') {
      // Tidy lines that fill each area edge to edge; every other row shifts half a phrase.
      let i = 0;
      for (const [a, b] of regs) {
        let row = 0;
        for (let y = o.rowGap * R * 0.5; y < H + R; row++) {
          const size = lerp(o.sizeMin, o.sizeMax, r(row, 2)) * R;
          g.font = `${o.fontWeight} ${size}px ${fam}`;
          let x = a - (row % 2 ? 0.5 : 0) * (g.measureText(texts[0]).width + o.gap * size);
          while (x < b) {
            const text = texts[i % texts.length];
            const w = g.measureText(text).width;
            out.push({ text, x: x + w / 2, y, a: ((r(i, 1) * 2 - 1) * o.angle * Math.PI) / 180, size, alpha: lerp(o.opacityMin, o.opacityMax, r(i, 7)) });
            x += w + o.gap * size; i++;
          }
          y += size * o.rowGap;
        }
      }
      return out;
    }
    const avg = texts.reduce((s, t) => s + t.length, 0) / Math.max(1, texts.length);
    const n = Math.max(1, Math.round(o.count * Math.min(1, 14 / Math.max(14, avg)))); // longer text, fewer repeats
    let i = 0;
    for (const [a, b] of regs) {
      const w = b - a, share = Math.max(1, Math.round((n * w * H) / total));
      const cols = Math.max(1, Math.round(Math.sqrt(share * (w / Math.max(1, H))))), rows = Math.ceil(share / cols);
      for (let k = 0; k < share; k++, i++) {
        const cx = k % cols, cy = Math.floor(k / cols);
        out.push({ text: texts[i % texts.length],
          x: a + ((cx + 0.1 + r(i, 3) * 0.8) / cols) * w, y: ((cy + 0.1 + r(i, 4) * 0.8) / rows) * H,
          a: ((r(i, 1) * 2 - 1) * o.angle * Math.PI) / 180, size: lerp(o.sizeMin, o.sizeMax, r(i, 2) * r(i, 2)) * R,
          alpha: lerp(o.opacityMin, o.opacityMax, r(i, 7)) });
      }
    }
    return out;
  }

  function draw() {
    W = innerWidth; H = innerHeight; dpr = Math.min(2, devicePixelRatio || 1);
    clip = sidesClip();
    wall.width = W * dpr; wall.height = H * dpr;
    const g = wall.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, W, H);
    phrases = clip ? layout(g) : [];
    if (!clip) return paintLens();
    g.save();
    g.beginPath();
    g.rect(clip.l[0], 0, clip.l[1] - clip.l[0], H);
    if (clip.r) g.rect(clip.r[0], 0, clip.r[1] - clip.r[0], H);
    g.clip();
    g.fillStyle = o.color; g.textAlign = 'center'; g.textBaseline = 'middle';
    const fam = fontFamily();
    for (const p of phrases) {
      g.globalAlpha = p.alpha;
      g.font = `${o.fontWeight} ${p.size}px ${fam}`;
      p.w = g.measureText(p.text).width;
      g.setTransform(dpr, 0, 0, dpr, p.x * dpr, p.y * dpr);
      g.rotate(p.a);
      g.fillText(p.text, 0, 0);
    }
    g.restore();
    paintLens();
  }

  function paintLens() {
    const d = o.radius * 2;
    if (lens.width !== Math.round(d * dpr)) { lens.width = lens.height = Math.round(d * dpr); lens.style.width = lens.style.height = d + 'px'; }
    if (lens.style.opacity !== '0') drawLens();
  }

  function drawLens() {
    const r = o.radius, s = lens.width, g = lens.getContext('2d');
    g.globalCompositeOperation = 'copy';
    g.drawImage(wall, (px - r) * dpr, (py - r) * dpr, s, s, 0, 0, s, s);
    g.globalCompositeOperation = 'destination-in';
    const f = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    f.addColorStop(0, '#000'); f.addColorStop(0.55, 'rgba(0,0,0,.55)'); f.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = f; g.fillRect(0, 0, s, s);
    g.globalCompositeOperation = 'source-over';
  }

  const inWall = (x) => clip && ((x >= clip.l[0] && x <= clip.l[1]) || (clip.r && x >= clip.r[0] && x <= clip.r[1]));

  function move() {
    raf = 0;
    const r = o.radius;
    drawLens();
    lens.style.transform = `translate3d(${px - r}px,${py - r}px,0)`;
    lens.style.opacity = '.9'; lens.style.transitionDuration = '120ms';
  }
  function hide() { lens.style.transitionDuration = ''; lens.style.opacity = '0'; }

  function onMove(e) {
    if (e.pointerType !== 'mouse' || still.matches || !alive) return;
    stopIntro();
    px = e.clientX; py = e.clientY;
    if (!inWall(px)) return hide();
    if (!raf) raf = requestAnimationFrame(move);
    clearTimeout(idle);
    if (o.idleMs !== 'always') idle = setTimeout(hide, o.idleMs);
  }

  // solo-flash: light up one whole saying, then another somewhere else.
  const fits = (p) => regions().some(([a, b]) => p.x - p.w / 2 >= a && p.x + p.w / 2 <= b) && p.y > p.size && p.y < H - p.size;
  function lightOne() {
    clearTimeout(tick);
    if (!alive || o.effect !== 'solo-flash') return;
    tick = setTimeout(lightOne, o.every);
    if (document.hidden || !clip) return;
    const pool = phrases.map((p, i) => i).filter((i) => i !== lastLit && fits(phrases[i]));
    if (!pool.length) return;
    const i = pool[Math.floor(Math.random() * pool.length)], p = phrases[i];
    lastLit = i;
    const el = document.createElement('span');
    el.textContent = p.text;
    el.style.cssText = `position:absolute;left:${p.x}px;top:${p.y}px;white-space:nowrap;color:${o.color};font:${o.fontWeight} ${p.size}px ${fontFamily()};transform:translate(-50%,-50%) rotate(${p.a}rad);opacity:0;will-change:opacity`;
    root.appendChild(el);
    lit?.remove(); lit = el;
    const fade = Math.min(700, o.showMs / 3), peak = still.matches ? 1 : p.alpha;
    el.animate(still.matches ? [{ opacity: peak }, { opacity: peak }]
      : [{ opacity: 0 }, { opacity: peak, offset: fade / o.showMs }, { opacity: peak, offset: 1 - fade / o.showMs }, { opacity: 0 }],
      { duration: o.showMs, easing: 'ease-in-out' }).finished.then(() => { el.remove(); if (lit === el) lit = null; }, () => {});
  }

  function stopIntro() { introAnims.forEach((a) => a.el.remove()); introAnims = []; }

  function runIntro() {
    if (!o.intro || still.matches || document.hidden || !phrases.length) return;
    stopIntro();
    const pool = phrases.filter((p) => inWall(p.x) && p.y > 40 && p.y < H - 40);
    const fam = fontFamily();
    for (let i = 0; i < Math.min(o.introCount, pool.length); i++) {
      const p = pool[Math.floor(Math.random() * pool.length)];
      const el = document.createElement('span');
      el.textContent = p.text;
      el.style.cssText = `position:absolute;left:${p.x}px;top:${p.y}px;white-space:nowrap;color:${o.color};font:${o.fontWeight} ${p.size}px ${fam};transform:translate(-50%,-50%) rotate(${p.a}rad);opacity:0;will-change:opacity`;
      root.appendChild(el);
      const a = el.animate([{ opacity: 0 }, { opacity: 0.3 * p.alpha, offset: 0.5 }, { opacity: 0 }],
        { duration: 900 + Math.random() * 900, delay: 200 + i * 450 + Math.random() * 150, easing: 'ease-in-out' });
      a.finished.then(() => el.remove(), () => {});
      introAnims.push({ el, a });
    }
  }

  let rz = 0;
  const onResize = () => { clearTimeout(rz); rz = setTimeout(draw, 150); };
  const onVis = () => { if (document.hidden) { stopIntro(); hide(); } };

  function mount(parent = document.body) {
    const flash = o.effect === 'solo-flash';
    if (!flash && !fine.matches) return api; // mouse-light needs a mouse
    parent.prepend(root);
    if (!flash) addEventListener('pointermove', onMove, { passive: true });
    addEventListener('resize', onResize, { passive: true });
    document.addEventListener('visibilitychange', onVis);
    document.documentElement.addEventListener('pointerleave', hide);
    (document.fonts?.ready ?? Promise.resolve()).then(() => { draw(); if (flash) lightOne(); else runIntro(); });
    return api;
  }

  function setText(text) {
    o.text = text;
    if (!root.isConnected) return api;
    lens.style.transitionDuration = '200ms'; lens.style.opacity = '0';
    lit?.remove(); lit = null; lastLit = -1;
    setTimeout(() => { draw(); if (o.effect === 'solo-flash') lightOne(); else if (o.idleMs === 'always' && inWall(px)) move(); }, 200);
    return api;
  }

  function update(next) { o = { ...o, ...next }; root.style.zIndex = o.zIndex; if (root.isConnected) draw(); return api; }

  function destroy() {
    alive = false; stopIntro(); clearTimeout(idle); clearTimeout(tick); clearTimeout(rz); cancelAnimationFrame(raf);
    removeEventListener('pointermove', onMove); removeEventListener('resize', onResize);
    document.removeEventListener('visibilitychange', onVis);
    document.documentElement.removeEventListener('pointerleave', hide);
    root.remove();
  }

  const api = { mount, setText, update, intro: runIntro, destroy, get options() { return { ...o }; } };
  return api;
}

window.createWordwall = createWordwall;
})();
